import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { Mutex } from 'async-mutex';
import { isEqual } from 'lodash';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, startWith } from 'rxjs';
import {
  calculateMaxNumRowsOrCols,
  checkStyleValueIsValid,
  extractNumberValueFromStyle,
  getCurrentStyleFromHTMLElement,
  isRightView,
  MARGIN_TOP_RIGHT_VIEW,
  NEW_VIDEO_CONTROLS_RESIZABLE_RIGHT_VIEW_MIN_WIDTH,
  RESIZABLE_RIGHT_VIEW_MIN_WIDTH,
  RIGHT_VIEW_HOST_MAX_NUMBER_OF_COLUMNS,
  RIGHT_VIEW_PARTICIPANT_MAX_NUMBER_OF_COLUMNS,
  RIGHT_VIEW_MAX_TILE_HEIGHT,
  RIGHT_VIEW_MIN_TILE_HEIGHT,
  RIGHT_VIEW_NAVIGATION_BUTTONS_HEIGHT,
  RIGHT_VIEW_NAVIGATION_BUTTONS_MARGIN,
  RIGHT_VIEW_BORDER_WIDTH,
  RIGHT_VIEW_SPACING,
  RIGHT_VIEW_CONTAINER_PADDING,
  TilesContainerParameters,
  TOP_VIEW_HEIGHT,
  TOP_VIEW_MAX_TILE_WIDTH,
  TOP_VIEW_MIN_TILE_WIDTH,
  TOP_VIEW_SPACING,
  VIDEO_CONTROLS_HEIGHT_RIGHT_VIEW,
  GALLERY_VIEW_BAR_HEIGHT,
  SPACE_BOTTOM_RIGHT_VIEW_TOWER,
  NEW_VIDEO_CONTROLS_HEIGHT_RIGHT_VIEW,
} from '../common/utils/tiles-packing-util';
import { FLAGS, FlagsService } from '../services/flags.service';
import { SessionSharedDataService, SessionView } from '../services/session-shared-data.service';
import { SpaceRepository } from '../state/space.repository';
import { PictureInPictureService } from '../sessions/session/services/picture-in-picture.service';
import { videoCallWindow } from '../services/device-and-browser-detector.service';

@UntilDestroy()
@Directive({
  selector: '[appTilesContainerObserver]',
})
export class TilesContainerObserverDirective
  implements OnChanges, OnInit, AfterViewInit, OnDestroy
{
  private _isCallConnected = false;
  @Input() currentSessionView: SessionView = SessionView.WHITEBOARD;
  @Input() isMobileView = false;
  @Input() numberOfTiles = 1;
  /** When true, the right-tower VCT strip is not rendered (e.g. observer mode), so do not reserve its height. */
  @Input() rightViewVideoCallControlshidden = false;
  @Input() set clearLatestEmittedChange(isCallConnected: boolean) {
    this._isCallConnected = isCallConnected;
    if (!isCallConnected) {
      this.latestEmittedChange = {};
    }
  }
  @Output() changeTilesContainerParameters: EventEmitter<TilesContainerParameters> =
    new EventEmitter();

  detectTilesContainerChangeMutex = new Mutex();

  containerResizeObserver?: ResizeObserver;

  latestEmittedChange?: any;

  // Used to debounce resizing change
  resizeChangeDetectedTimer: any;
  readonly APPLY_RESIZE_CHANGE_DELTA = 150;

  private unlistenMouseDown!: () => void;
  private unlistenMouseUp!: () => void;
  private unlistenWindowResize!: () => void;

  isResizing = false;
  resizedWidthRightView?: number;
  resizedHeightRightView?: number;
  viewsAllowVideoCallComponentResize = [SessionView.WHITEBOARD, SessionView.FULLSCREEN_APP];

  private rightViewMinTileHeight = RIGHT_VIEW_MIN_TILE_HEIGHT;
  private resizedRightViewMinWidth = RESIZABLE_RIGHT_VIEW_MIN_WIDTH;

  // this value is used to set the maximum number of columns in the right view
  // it is changable because we need to support more columns for hosts
  private maxNumberOfColsSetting = RIGHT_VIEW_PARTICIPANT_MAX_NUMBER_OF_COLUMNS;

  constructor(
    private elRef: ElementRef,
    private zone: NgZone,
    private renderer2: Renderer2,
    private flagsService: FlagsService,
    private spaceRepo: SpaceRepository,
    private sharedSessionDataService: SessionSharedDataService,
    private pictureInPictureService: PictureInPictureService,
  ) {}
  ngOnDestroy(): void {
    if (this.flagsService.featureFlagsVariables.dynamic_right_view?.resizable_tower) {
      this.unlistenMouseDown();
    }
    this.unlistenWindowResize();
    this.containerResizeObserver?.disconnect();
  }
  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      // Handle window size change for right view, and top view
      this.unlistenWindowResize = this.renderer2.listen('window', 'resize', () => {
        if (this.viewsAllowVideoCallComponentResize.includes(this.currentSessionView)) {
          this.detectTilesContainerChange();
        }
      });
    });
    if (this.flagsService.featureFlagsVariables.dynamic_right_view?.resizable_tower) {
      this.handleResizingDetection();
    }
    if (this.viewsAllowVideoCallComponentResize.includes(this.currentSessionView)) {
      this.publishNewTilesContainerParameters(this.calculateRightViewParameters());
    }
  }

  private handleResizingDetection() {
    // Used Renderer2 to be able to run these listeners outside angular
    // as HostListener will cause CD cycle regardless its body will be executed or not.
    // Also starting listening for mouse up event when it's necessary
    this.zone.runOutsideAngular(() => {
      this.unlistenMouseDown = this.renderer2.listen('window', 'mousedown', (e) => {
        if (
          this.viewsAllowVideoCallComponentResize.includes(this.currentSessionView) &&
          this._isCallConnected &&
          (e.target.id === 'overlay-parent-container' ||
            e.target.id === 'overlay-parent-container-resizer') &&
          !this.isResizing
        ) {
          this.isResizing = true;
          this.unlistenMouseUp = this.renderer2.listen('window', 'mouseup', () => {
            if (
              this.viewsAllowVideoCallComponentResize.includes(this.currentSessionView) &&
              this._isCallConnected &&
              this.isResizing
            ) {
              this.unlistenMouseUp();
              this.isResizing = false;
              this.detectTilesContainerChange(false, true);
            }
          });
        }
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.numberOfTiles && isRightView(this.currentSessionView, this.isMobileView)) ||
      changes.currentSessionView ||
      changes.isMobileView ||
      changes.rightViewVideoCallControlshidden
    ) {
      this.detectTilesContainerChange();
    }
  }

  detectTilesContainerChange(isResizingRightView = false, forcePublishing = false) {
    this.detectTilesContainerChangeMutex.runExclusive(() => {
      const availableWidth = this.calculateAvailableWidth();
      const availableHeight = this.calculateAvailableHeight();

      switch (this.currentSessionView) {
        // Has different ways to calculate available width, and height
        case SessionView.WHITEBOARD:
        case SessionView.FULLSCREEN_APP:
          if (this.isMobileView) {
            this.publishNewTilesContainerParameters(this.calculateTopViewParameters());
          } else {
            this.publishNewTilesContainerParameters(
              this.calculateRightViewParameters(isResizingRightView),
              forcePublishing,
            );
          }
          break;
        case SessionView.GALLERY_VIEW:
          // Deprecated: gallery sizing is handled by optimized service; emit empty params
          this.publishNewTilesContainerParameters({});
          break;
        case SessionView.EXPANDED:
          // Expanded sizing handled by optimized service as well; emit empty
          this.publishNewTilesContainerParameters({});
          break;
        case SessionView.FULLSCREEN:
          this.publishNewTilesContainerParameters(
            this.calculateFullScreenViewParameters(availableWidth, availableHeight),
          );
          break;
        default:
          return;
      }
    });
  }

  private calculateAvailableHeight(): number {
    const screenHeight = videoCallWindow().innerHeight;
    const topNavBarHeight = this.calculateTopNavBarHeight();
    const videoControlsGalleryViewHeight = this.calculatevideoControlsGalleryViewHeight();

    return screenHeight - topNavBarHeight - videoControlsGalleryViewHeight;
  }

  private calculateTopNavBarHeight(): number {
    return this.pictureInPictureService.isPIPActive
      ? 0
      : this.sharedSessionDataService.topControlsHeight;
  }

  private calculateAvailableWidth(): number {
    const screenWidth = this.elRef.nativeElement.offsetWidth ?? videoCallWindow().innerWidth;

    return screenWidth;
  }

  // Mobile top view layout
  // Top view has a fixed height, so we are looking for maximum number for cols that can fit the available width
  private calculateTopViewParameters(): any {
    const availableWidth = window.innerWidth;

    const maxColsNum = calculateMaxNumRowsOrCols(
      availableWidth,
      TOP_VIEW_MIN_TILE_WIDTH,
      TOP_VIEW_SPACING,
    );

    const newTilesContainerParameters: TilesContainerParameters = {
      availableWidth: availableWidth,
      availableHeight: TOP_VIEW_HEIGHT,
      maxColsNum: maxColsNum,
      maxRowsNum: 1,
      minTileWidth: TOP_VIEW_MIN_TILE_WIDTH,
      minTileHeight: TOP_VIEW_HEIGHT, // Minimum tile height must be the same for the available height
      aspectRatio: TOP_VIEW_MIN_TILE_WIDTH / TOP_VIEW_HEIGHT,
      maxTileWidth: TOP_VIEW_MAX_TILE_WIDTH, // To handle the case of there is only 1 user in the call, so it won't take the whole width
      spaceBetweenTiles: TOP_VIEW_SPACING,
      anchorTopWithoutVerticallyCentered: 0,
      anchorLeftWithoutHorizontallyCentered: 0,
    };

    return newTilesContainerParameters;
  }

  // Right view has a fixed cols number = 1, so we are looking for how many tiles can fit the available height
  private calculateRightViewParameters(isResizing = false): any {
    // Using this pattern for getting margin top, width, or height to handle the case of showing the right tower once you started/joined the call
    // This way lets us to get the written values from SCSS style sheet

    // Div that holds mic, cam, share screen, ... etc buttons
    let videoControlsFirstDivHeight = this.getDivHeightRightView(
      'video-controls-first-div',
      VIDEO_CONTROLS_HEIGHT_RIGHT_VIEW,
    );

    // Div holds end call, expand, minimize, ... etc buttons
    let videoControlsSecondDivHeight = this.getDivHeightRightView(
      'video-controls-second-div',
      VIDEO_CONTROLS_HEIGHT_RIGHT_VIEW,
    );

    if (this.flagsService.isFlagEnabled(FLAGS.ENABLE_RIGHT_VIEW_NEW_VIDEO_CONTROLS)) {
      videoControlsFirstDivHeight = NEW_VIDEO_CONTROLS_HEIGHT_RIGHT_VIEW;
      videoControlsSecondDivHeight = 0;
    }

    if (this.rightViewVideoCallControlshidden) {
      videoControlsFirstDivHeight = 0;
      videoControlsSecondDivHeight = 0;
    }

    let rightViewMarginTop = MARGIN_TOP_RIGHT_VIEW;
    const parentContainerStyle = getCurrentStyleFromHTMLElement(this.elRef.nativeElement);
    const top = parentContainerStyle.top;
    if (checkStyleValueIsValid(top)) {
      rightViewMarginTop = extractNumberValueFromStyle(top);
    }

    // Setting current available width based on resizing
    if (isResizing) {
      this.resizedWidthRightView = this.elRef.nativeElement.offsetWidth;
    }

    // Handle hitting our limits while resizing
    if (!isResizing && this.resizedWidthRightView) {
      if (this.checkResizingUpperBound()) {
        this.resizedWidthRightView = window.innerWidth / 2;
      }

      if (this.checkResizingLowerBound()) {
        this.resizedWidthRightView = this.resizedRightViewMinWidth;
      }
    }

    // Calculate the maximum available height that the tower can take on the client screen
    const rightViewHeight = window.innerHeight - rightViewMarginTop - SPACE_BOTTOM_RIGHT_VIEW_TOWER;

    const availableHeight =
      rightViewHeight -
      videoControlsFirstDivHeight -
      videoControlsSecondDivHeight -
      RIGHT_VIEW_NAVIGATION_BUTTONS_HEIGHT -
      2 * RIGHT_VIEW_NAVIGATION_BUTTONS_MARGIN -
      RIGHT_VIEW_CONTAINER_PADDING -
      2 * RIGHT_VIEW_BORDER_WIDTH;

    let maxRowsNum1Panel = calculateMaxNumRowsOrCols(
      availableHeight,
      this.rightViewMinTileHeight,
      RIGHT_VIEW_SPACING,
    );
    let aspectRatio1Panel = 1;
    let maxColsNum = Math.min(
      this.maxNumberOfColsSetting,
      Math.ceil(this.numberOfTiles / maxRowsNum1Panel),
    );
    [maxRowsNum1Panel, aspectRatio1Panel, maxColsNum] = this.calculatePanelResizedTowerParameters(
      availableHeight,
      this.getAvailableWidthRightView(maxColsNum),
      maxRowsNum1Panel,
      this.maxNumberOfColsSetting,
    );

    const availableWidth = this.getAvailableWidthRightView(maxColsNum);
    const newTilesContainerParameters: TilesContainerParameters = {
      isResized: !!this.resizedWidthRightView,
      availableWidth: availableWidth,
      availableHeight: availableHeight,
      maxColsNum: maxColsNum,
      maxRowsNum: maxRowsNum1Panel,
      minTileWidth: this.rightViewMinTileHeight,
      minTileHeight: this.rightViewMinTileHeight,
      aspectRatio: aspectRatio1Panel,
      spaceBetweenTiles: RIGHT_VIEW_SPACING,
      skipSettingComponentDimsWidth: isResizing,
      anchorTopWithoutVerticallyCentered: RIGHT_VIEW_CONTAINER_PADDING,
      anchorLeftWithoutHorizontallyCentered: RIGHT_VIEW_CONTAINER_PADDING,
      videoControlsFirstDivHeight: videoControlsFirstDivHeight,
      videoControlsSecondDivHeight: videoControlsSecondDivHeight,
    };

    return newTilesContainerParameters;
  }

  private getAvailableWidthRightView(maxColsNumber: number) {
    // if the user resizing the tower, subtract container padding and border to get tile-available width
    if (this.resizedWidthRightView) {
      return (
        this.resizedWidthRightView - 2 * RIGHT_VIEW_CONTAINER_PADDING - 2 * RIGHT_VIEW_BORDER_WIDTH
      );
    }
    // calculate the needed tile-area width (container padding is added back by setComponetDimensionsWithRightView)
    // if the number of cols is 1, use max tile height as width to make the tiles look big
    return maxColsNumber === 1
      ? RIGHT_VIEW_MAX_TILE_HEIGHT
      : maxColsNumber * this.rightViewMinTileHeight + (maxColsNumber - 1) * RIGHT_VIEW_SPACING;
  }

  private getDivHeightRightView(divId: string, defaultValue: number): number {
    let divHeight = defaultValue;
    const div = document.getElementById(divId);
    if (div) {
      const divStyle = getCurrentStyleFromHTMLElement(div);
      const height = divStyle.height;
      if (checkStyleValueIsValid(height)) {
        divHeight = extractNumberValueFromStyle(height);
      }
    }
    return divHeight;
  }

  private checkResizingUpperBound(): boolean {
    return !!this.resizedWidthRightView && this.resizedWidthRightView > window.innerWidth / 2;
  }

  private checkResizingLowerBound(): boolean {
    return (
      !!this.resizedWidthRightView && this.resizedWidthRightView < this.resizedRightViewMinWidth
    );
  }

  private calculatePanelResizedTowerParameters(
    availableHeight: number,
    resizedWidthRightView: number,
    maxRowsNum1PanelNonResizable: number,
    maxNumberOfColsSetting: number,
  ): number[] {
    const maxRows = maxRowsNum1PanelNonResizable;
    let aspectRatio = 1;
    // we greedly fill column by column with maxRows before moving to the next column
    const maxColumns = Math.max(
      1,
      Math.min(
        maxNumberOfColsSetting,
        Math.floor(
          (resizedWidthRightView + RIGHT_VIEW_SPACING) /
            (this.rightViewMinTileHeight + RIGHT_VIEW_SPACING),
        ),
        Math.ceil(this.numberOfTiles / maxRows),
      ),
    );
    // this is the number of rows that will be displayed per column
    const perColunmTilesCount = Math.min(
      Math.ceil(this.numberOfTiles / maxColumns),
      maxRowsNum1PanelNonResizable,
    );
    const maximumTileHeight =
      (availableHeight - (perColunmTilesCount - 1) * RIGHT_VIEW_SPACING) / perColunmTilesCount;
    // tile width in the range [minTileHeight, maximumTileHeight*16/9]
    const tileWidth = Math.min(
      (maximumTileHeight * 16) / 9,
      (resizedWidthRightView - (maxColumns - 1) * RIGHT_VIEW_SPACING) / maxColumns,
    );
    // aspect ratio =  widht / height
    aspectRatio = Math.max(1, tileWidth / maximumTileHeight);
    return [maxRows, aspectRatio, maxColumns];
  }

  private calculateFullScreenViewParameters(availableWidth: number, availableHeight: number): any {
    const newTilesContainerParameters: TilesContainerParameters = {
      availableWidth: availableWidth,
      availableHeight: availableHeight,
      maxColsNum: 1,
      maxRowsNum: 1,
      minTileWidth: availableWidth,
      minTileHeight: availableHeight,
      aspectRatio: availableWidth / availableHeight,
      spaceBetweenTiles: 0,
      anchorTopWithoutVerticallyCentered: 0,
      anchorLeftWithoutHorizontallyCentered: 0,
    };
    return newTilesContainerParameters;
  }

  private calculatevideoControlsGalleryViewHeight(): number {
    return document.getElementById('video-controls')?.offsetHeight || GALLERY_VIEW_BAR_HEIGHT;
  }

  private publishNewTilesContainerParameters(
    newTilesContainerParameters: any,
    forcePublishing = false,
  ) {
    if (
      [SessionView.GALLERY_VIEW, SessionView.EXPANDED].includes(this.currentSessionView) ||
      !isEqual(this.latestEmittedChange, newTilesContainerParameters) ||
      forcePublishing
    ) {
      this.changeTilesContainerParameters.emit(newTilesContainerParameters);
      this.latestEmittedChange = newTilesContainerParameters;
    }
  }

  ngOnInit(): void {
    combineLatest([
      this.flagsService.featureFlagsChanged.pipe(startWith(undefined)),
      this.spaceRepo.isCurrentUserHost$,
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([_, isHost]) => {
        let needsRecalculateContainerParams = false;
        // generic variables
        const dynamicRightWiewFlagVariables =
          this.flagsService.featureFlagsVariables.dynamic_right_view;
        const newMinTileHeight =
          (dynamicRightWiewFlagVariables?.minimum_tile_height as number) ||
          RIGHT_VIEW_MIN_TILE_HEIGHT;

        const isNewRightViewVideoControlsEnabled = this.flagsService.isFlagEnabled(
          FLAGS.ENABLE_RIGHT_VIEW_NEW_VIDEO_CONTROLS,
        );
        const newMinResizedRightTowerWidth = isNewRightViewVideoControlsEnabled
          ? (dynamicRightWiewFlagVariables?.resized_right_view_min_width_new_video_controls as number) ||
            NEW_VIDEO_CONTROLS_RESIZABLE_RIGHT_VIEW_MIN_WIDTH
          : (dynamicRightWiewFlagVariables?.resized_right_view_min_width as number) ||
            RESIZABLE_RIGHT_VIEW_MIN_WIDTH;
        if (
          newMinTileHeight !== this.rightViewMinTileHeight ||
          newMinResizedRightTowerWidth !== this.resizedRightViewMinWidth
        ) {
          this.rightViewMinTileHeight = newMinTileHeight;
          this.resizedRightViewMinWidth = newMinResizedRightTowerWidth;
          needsRecalculateContainerParams = true;
        }

        if (isHost) {
          const newMaxNumberOfCols =
            (dynamicRightWiewFlagVariables?.host_max_columns as number) ||
            RIGHT_VIEW_HOST_MAX_NUMBER_OF_COLUMNS;
          if (this.maxNumberOfColsSetting !== newMaxNumberOfCols) {
            this.maxNumberOfColsSetting = newMaxNumberOfCols;
            needsRecalculateContainerParams = true;
          }
        } else {
          const newMaxNumberOfCols =
            (dynamicRightWiewFlagVariables?.participant_max_columns as number) ||
            RIGHT_VIEW_PARTICIPANT_MAX_NUMBER_OF_COLUMNS;
          if (this.maxNumberOfColsSetting !== newMaxNumberOfCols) {
            this.maxNumberOfColsSetting = newMaxNumberOfCols;
            needsRecalculateContainerParams = true;
          }
        }

        if (needsRecalculateContainerParams) {
          this.detectTilesContainerChange();
        }
      });

    this.listenToVideoCallComponentResize();
  }

  private listenToVideoCallComponentResize() {
    this.pictureInPictureService.isPIPActive$
      .pipe(untilDestroyed(this))
      .subscribe((isPIPActive) => {
        this.containerResizeObserver?.disconnect();
        /**
         * Please note that we force running the callback of the resize observable outside of the zone
         * because the callback by default should run inside the zone by adding
         * import 'zone.js/dist/zone-patch-resize-observer'; to the polyfills.ts file.
         * However, due to a bug in Angular, it sometimes runs inside the zone and sometimes outside the zone when re-creating the observer.
         * So we need to  manually run it inside the zone
         * using zone.run() to ensure consistent behavior.
         */
        this.zone.runOutsideAngular(() => {
          this.containerResizeObserver = new ResizeObserver(() => {
            this.zone.run(() => {
              this.changeInSizeDetected();
            });
          });
          if (isPIPActive) {
            this.containerResizeObserver.observe(videoCallWindow().document.body);
          } else {
            this.containerResizeObserver.observe(this.elRef.nativeElement);
          }
        });
      });
  }

  changeInSizeDetected() {
    if (!this.viewsAllowVideoCallComponentResize.includes(this.currentSessionView)) {
      this.detectTilesContainerChange();
    }
    if (
      this.viewsAllowVideoCallComponentResize.includes(this.currentSessionView) &&
      this.isResizing
    ) {
      this.detectTilesContainerChange(true);
    }
  }
}
